import axios from "axios";
import { FHIR_METHODS } from "config/constants/adapter_constants";
import { getTenantFromToken } from "config/helpers/common_helper";
import { auth } from "firebase-setup";
import jwtDecode from "jwt-decode";
import _ from "lodash";

const generateInterceptors = (
  AxiosInstance,
  contentType = "application/json"
) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        // config.headers["x-enliv-tenant-id"] = jwtPayload?.firebase?.tenant;
        config.headers["Content-Type"] = contentType;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  AxiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const user = auth?.currentUser;
          if (user) {
            const token = await user.getIdToken(true);
            localStorage.setItem("jwtToken", token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return AxiosInstance(originalRequest);
          }
        } catch (err) {}
      }
      return Promise.reject(error);
    }
  );
};

let HTTPRequest = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  // this is for /api/fhir and /api
});

generateInterceptors(HTTPRequest);

export const HTTPAidaBaseService = axios.create({
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL + "/api/agent/v1",
});

export const HTTPAidaAIBaseService = axios.create({
  // MIA analytics summary lives on project-o-fastapi (orie-ai-service) at /v1/mia/...,
  // reached via the LB /api/agent/{query} rule. The old `/ai` segment routed to the
  // retired samaras-mia-api-service-demo and now 404s, so target /api/agent/v1 directly.
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL + "/api/agent/v1",
});

export const HTTPAidaBaseServiceMultipartFormData = axios.create({
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL + "/api/agent/v1",
  // this is for /api/v1
});

generateInterceptors(HTTPAidaBaseService);
generateInterceptors(HTTPAidaAIBaseService);
generateInterceptors(
  HTTPAidaBaseServiceMultipartFormData,
  "multipart/form-data"
);

let HTTPRequestUnauthenticated = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  // this is for /api/auth and /api/auth2
});

const FHIRSearch = (module, params) => {
  return HTTPRequest.get(
    `fhir/tenants/${getTenantFromToken()}/search/${module}${
      params ? "?" + params : ""
    }`
  );
};

const FHIRSave = (action, data) => {
  if (action === "executeBundle") {
    return HTTPRequest?.post(
      `fhir/tenants/${getTenantFromToken()}/` + "executeBundle",
      data
    );
  }
  let methodIdx = FHIR_METHODS.findIndex((f) => f.TYPE === action);
  if (methodIdx < 0) {
    console.error(
      `API Call Failed --> Please check if ${action} is a valid action.`
    );
    return false;
  }
  return HTTPRequest?.[_.lowerCase(action)](
    `fhir/tenants/${getTenantFromToken()}/` + FHIR_METHODS[methodIdx].RESOURCE,
    data
  );
};

const FHIRDelete = (resourceType, resourceId) => {
  return HTTPRequest?.delete(
    `fhir/tenants/${getTenantFromToken()}/deleteResource?resourceType=${resourceType}&resourceId=${resourceId}`
  );
};

const NokiService = (action, url = "", data) => {
  return HTTPAidaBaseService?.[_.lowerCase(action)](url, data);
};

export const HTTPAgentService = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  // this is with end point as /api/ai/v1
});
export const IntakeAgentService = axios.create({
  baseURL: process.env.REACT_APP_INTAKE_URL,
});
export const SLICFHIRHTTP = axios.create({
  baseURL: process.env.REACT_APP_API_URL + "/ai/v1",
  // this is also /api/ai/v1
});

generateInterceptors(HTTPAgentService);
generateInterceptors(SLICFHIRHTTP);

export const HTTPCtmsService = axios.create({
  baseURL: process.env.REACT_APP_CTMS_SERVICE_URL,
  // samaras-ctms-service — /integrations/...
});
generateInterceptors(HTTPCtmsService);

const OrieService = (action, url = "", data) => {
  return HTTPAgentService?.[_.lowerCase(action)](url, data);
};

const NokiEndpointService = ({ action, endpoint = "", payload = {} }) => {
  return SLICFHIRHTTP?.[_.lowerCase(action)](endpoint, payload);
};

export default HTTPRequest; // api/, api/auth, api/auth2, api/fhir
export {
  FHIRSearch, //api/
  FHIRSave, //api/
  FHIRDelete, //api/
  NokiService, //api/agent/ai
  HTTPRequestUnauthenticated, //api/auth and /api/auth2
  OrieService, //api/ai/
  NokiEndpointService, //api/ai/
};
